import { useState, useEffect } from 'react'
import { networkClient } from '../network/client'

interface LogicPuzzleData {
  puzzleId: string
  type: 'logic'
  difficulty: string
  title: string
  description: string
  inputs: string[]
  expression: string
}

interface LogicPuzzlePanelProps {
  puzzle: LogicPuzzleData
  onClose: () => void
}

export default function LogicPuzzlePanel({ puzzle, onClose }: LogicPuzzlePanelProps) {
  const [inputStates, setInputStates] = useState<boolean[]>([])
  const [feedback, setFeedback] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [hintsUsed, setHintsUsed] = useState(0)
  const [solved, setSolved] = useState(false)
  const [solvedData, setSolvedData] = useState<{ points: number; message: string; correctInputs: boolean[]; stabilityRestored?: number } | null>(null)

  // Initialize input states
  useEffect(() => {
    setInputStates(puzzle.inputs.map(() => false))
  }, [puzzle.inputs])

  useEffect(() => {
    const room = networkClient.getGameRoom()
    if (!room) return

    const handleWrong = (data: { message: string }) => {
      setFeedback(data.message)
      setTimeout(() => setFeedback(null), 3000)
    }

    const handleHint = (data: { hint: string; hintsUsed: number }) => {
      setHint(data.hint)
      setHintsUsed(data.hintsUsed)
    }

    const handleSolved = (data: any) => {
      if (data.puzzleId === puzzle.puzzleId && data.type === 'logic') {
        setSolved(true)
        setSolvedData(data)
      }
    }

    room.onMessage('puzzle_wrong', handleWrong)
    room.onMessage('puzzle_hint', handleHint)
    room.onMessage('puzzle_solved', handleSolved)

    return () => {}
  }, [puzzle.puzzleId])

  const toggleInput = (index: number) => {
    const newStates = [...inputStates]
    newStates[index] = !newStates[index]
    setInputStates(newStates)
  }

  const handleSubmit = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('submit_logic_answer', {
        puzzleId: puzzle.puzzleId,
        inputStates: inputStates,
      })
    }
  }

  const requestHint = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_hint', { puzzleId: puzzle.puzzleId })
    }
  }

  const getDifficultyColor = (diff: string) => {
    switch (diff) {
      case 'easy': return 'text-green-400'
      case 'medium': return 'text-yellow-400'
      case 'hard': return 'text-red-400'
      default: return 'text-white'
    }
  }

  const getDifficultyLabel = (diff: string) => {
    switch (diff) {
      case 'easy': return 'Latwa'
      case 'medium': return 'Srednia'
      case 'hard': return 'Trudna'
      default: return diff
    }
  }

  if (solved && solvedData) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-800 border-2 border-emerald-neon rounded-xl p-8 max-w-lg text-center">
          <h2 className="text-3xl font-bold text-emerald-neon mb-4">Obwod dziala!</h2>
          <p className="text-xl text-white mb-4">{solvedData.message}</p>
          <div className="text-4xl font-bold text-yellow-400 mb-2">+{solvedData.points} pkt</div>
          {solvedData.stabilityRestored && (
            <div className="text-lg text-emerald-neon mb-4">+{solvedData.stabilityRestored}% stabilnosci</div>
          )}

          <div className="mb-6 p-4 bg-gray-900 rounded-lg">
            <p className="text-gray-400 mb-2">Poprawna konfiguracja:</p>
            <div className="flex justify-center gap-4">
              {puzzle.inputs.map((input, idx) => (
                <div key={input} className="text-center">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg mb-1
                    ${solvedData.correctInputs[idx] ? 'bg-emerald-neon text-gray-900' : 'bg-gray-600 text-gray-400'}`}>
                    {solvedData.correctInputs[idx] ? '1' : '0'}
                  </div>
                  <div className="text-sm text-gray-400">{input}</div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            className="px-8 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
          >
            Kontynuuj
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-gray-600 rounded-xl p-6 max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">{puzzle.title}</h2>
            <p className={`text-sm ${getDifficultyColor(puzzle.difficulty)}`}>
              Trudnosc: {getDifficultyLabel(puzzle.difficulty)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Instructions */}
        <p className="text-gray-300 mb-4">{puzzle.description}</p>

        {/* Expression display */}
        <div className="bg-gray-900 rounded-lg p-4 mb-6 text-center">
          <p className="text-gray-400 text-sm mb-2">Wyrazenie logiczne:</p>
          <p className="text-2xl font-mono text-emerald-neon">{puzzle.expression}</p>
        </div>

        {/* Input switches */}
        <div className="flex justify-center gap-6 mb-6">
          {puzzle.inputs.map((input, idx) => (
            <div key={input} className="text-center">
              <div className="text-lg font-bold text-white mb-2">{input}</div>
              <button
                onClick={() => toggleInput(idx)}
                className={`w-20 h-20 rounded-xl font-bold text-2xl transition-all transform hover:scale-105
                  ${inputStates[idx]
                    ? 'bg-emerald-neon text-gray-900 shadow-lg shadow-emerald-neon/50'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
              >
                {inputStates[idx] ? 'ON' : 'OFF'}
              </button>
              <div className="text-xs text-gray-500 mt-1">
                {inputStates[idx] ? 'TRUE (1)' : 'FALSE (0)'}
              </div>
            </div>
          ))}
        </div>

        {/* Visual output indicator */}
        <div className="text-center mb-6">
          <div className="text-gray-400 text-sm mb-2">Wyjscie:</div>
          <div className={`inline-block w-16 h-16 rounded-full flex items-center justify-center
            ${inputStates.some(s => s) ? 'bg-yellow-500/30 border-2 border-yellow-500' : 'bg-gray-700 border-2 border-gray-600'}`}>
            <span className="text-2xl">?</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Kliknij "Sprawdz" aby przetestowac</p>
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          className="w-full mb-4 px-6 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
        >
          Sprawdz obwod
        </button>

        {/* Feedback */}
        {feedback && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 text-red-300 rounded-lg">
            {feedback}
          </div>
        )}

        {/* Hint */}
        {hint && (
          <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 text-blue-300 rounded-lg">
            <strong>Podpowiedz:</strong> {hint}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            onClick={requestHint}
            disabled={hintsUsed >= 3}
            className="px-4 py-2 text-gray-400 hover:text-yellow-400 disabled:opacity-50"
          >
            Popros o podpowiedz ({hintsUsed}/3)
          </button>

          <div className="text-gray-500 text-sm">
            Nagroda: {puzzle.difficulty === 'easy' ? 150 : puzzle.difficulty === 'medium' ? 250 : 500} pkt
          </div>
        </div>
      </div>
    </div>
  )
}
